import { createSlice } from '@reduxjs/toolkit';
import type { OrganizationSliceState } from '@/types/organization.types';

import { fetchOrganizationDashboard } from '@/store/thunks/organization/fetchOrganizationDashboard';
import { fetchOrgProfile } from '@/store/thunks/organization/fetchOrgProfile';
import { fetchOrgAdmins } from '@/store/thunks/organization/fetchOrgAdmins';
import { fetchOrgSeats } from '@/store/thunks/organization/fetchOrgSeats';
import { fetchOrgUsers } from '@/store/thunks/organization/fetchOrgUsers';
import { toggleAdminActiveThunk } from '@/store/thunks/administrators/toggleAdminActiveThunk';
import { forceLogoutAdminThunk } from '@/store/thunks/administrators/forceLogoutAdminThunk';
import { forceLoginResetAdminThunk } from '@/store/thunks/administrators/forceLoginResetAdminThunk';
import { deleteAdminThunk } from '@/store/thunks/administrators/deleteAdminThunk';

const MIN = 0;

const initialState: OrganizationSliceState = {
  data:  {
      name: '',
      customerSince: undefined,
      phone: '',
      address: { line1: '', city: '', state: '', zip: '' },
      administrators: [],
      internalSeats: { available: MIN, active: MIN, new: MIN, inactive: MIN },
      externalSeats: { available: MIN, active: MIN, new: MIN, inactive: MIN },
      organizationUsers: [],
      appDomain: '',
    },
  loading: { profile: false, admins: false, seats: false, orgUsers: false },
  error: undefined,
  rowOps: {},
  loaded: { dashboard: false },
};

/** Utility to safely update row-operation flags */
const setRowLoading = (
  state: OrganizationSliceState,
  id: string,
  loading: boolean,
  error?: string
) => {
  state.rowOps[id] = { loading, error };
};

const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    /** ---- Dashboard ---- */
    builder
      .addCase(fetchOrganizationDashboard.pending, (state) => {
        state.loading = { profile: true, admins: true, seats: true, orgUsers: true };
        state.error = undefined;
        state.loaded!.dashboard = false;
      })
      .addCase(fetchOrganizationDashboard.fulfilled, (state, action) => {
        state.loading = { profile: false, admins: false, seats: false, orgUsers: false };
        state.data = action.payload;
        state.loaded!.dashboard = true;
      })
      .addCase(fetchOrganizationDashboard.rejected, (state, action) => {
        state.loading = { profile: false, admins: false, seats: false, orgUsers: false };
        state.error = action.payload ?? 'Failed to load organization dashboard';
      });

    /** ---- Profile ---- */
    builder
      .addCase(fetchOrgProfile.pending, (state) => {
        state.loading.profile = true;
        state.error = undefined;
      })
      .addCase(fetchOrgProfile.fulfilled, (state, action) => {
        state.loading.profile = false;
        if (state.data !== null) {
          const { payload : {name, customerSince, phone, address, appDomain }} = action;
          state.data.name = name;
          state.data.customerSince = customerSince;
          state.data.phone = phone;
          state.data.address = address;
          state.data.appDomain = appDomain;
        }
      })
      .addCase(fetchOrgProfile.rejected, (state, action) => {
        state.loading.profile = false;
        state.error = action.payload ?? 'Failed to load organization profile';
      });

    /** ---- Admins ---- */
    builder
      .addCase(fetchOrgAdmins.pending, (state) => {
        state.loading.admins = true;
        state.error = undefined;
      })
      .addCase(fetchOrgAdmins.fulfilled, (state, action) => {
        state.loading.admins = false;
        if (state.data !== null) state.data.administrators = action.payload;
      })
      .addCase(fetchOrgAdmins.rejected, (state, action) => {
        state.loading.admins = false;
        state.error = action.payload ?? 'Failed to load administrators';
      });

    /** ---- Seats ---- */
    builder
      .addCase(fetchOrgSeats.pending, (state) => {
        state.loading.seats = true;
        state.error = undefined;
      })
      .addCase(fetchOrgSeats.fulfilled, (state, action) => {
        state.loading.seats = false;
        if (state.data !== null) {
          state.data.internalSeats = action.payload.internalSeats;
          state.data.externalSeats = action.payload.externalSeats;
        }
      })
      .addCase(fetchOrgSeats.rejected, (state, action) => {
        state.loading.seats = false;
        state.error = action.payload;
      });

    /** ---- Users ---- */
    builder
      .addCase(fetchOrgUsers.pending, (state) => {
        state.loading.orgUsers = true;
        state.error = undefined;
      })
      .addCase(fetchOrgUsers.fulfilled, (state, action) => {
        state.loading.orgUsers = false;
        if (state.data !== null) state.data.organizationUsers = action.payload;
      })
      .addCase(fetchOrgUsers.rejected, (state, action) => {
        state.loading.orgUsers = false;
        state.error = action.payload ?? 'Failed to load users';
      });

    /** ---- Admin Row Actions ---- */
    builder
      .addCase(toggleAdminActiveThunk.pending, (state, action) => {
        setRowLoading(state, action.meta.arg.id, true);
      })
      .addCase(toggleAdminActiveThunk.fulfilled, (state, action) => {
        const {meta : { arg : {id}}} = action;
        setRowLoading(state, id, false);
        const admins = state.data?.administrators ?? [];
        const idx = admins.findIndex((x) => x.id === id);
        if (idx >= MIN) {
          admins[idx] = {
            ...admins[idx],
            status: admins[idx].status === 'Active' ? 'Inactive' : 'Active',
          };
        }
      })
      .addCase(toggleAdminActiveThunk.rejected, (state, action) => {
        setRowLoading(state, action.meta.arg.id, false, action.error.message);
      })

      .addCase(forceLogoutAdminThunk.pending, (state, action) => {
        setRowLoading(state, action.meta.arg.id, true);
      })
      .addCase(forceLogoutAdminThunk.fulfilled, (state, action) => {
        setRowLoading(state, action.meta.arg.id, false);
      })
      .addCase(forceLogoutAdminThunk.rejected, (state, action) => {
        setRowLoading(state, action.meta.arg.id, false, action.error.message);
      })

      .addCase(forceLoginResetAdminThunk.pending, (state, action) => {
        setRowLoading(state, action.meta.arg.id, true);
      })
      .addCase(forceLoginResetAdminThunk.fulfilled, (state, action) => {
        setRowLoading(state, action.meta.arg.id, false);
      })
      .addCase(forceLoginResetAdminThunk.rejected, (state, action) => {
        setRowLoading(state, action.meta.arg.id, false, action.error.message);
      })

      .addCase(deleteAdminThunk.pending, (state, action) => {
        setRowLoading(state, action.meta.arg.id, true);
      })
      .addCase(deleteAdminThunk.fulfilled, (state, action) => {
        const { meta: { arg: {id}}} = action
        setRowLoading(state, id, false);
      })
      .addCase(deleteAdminThunk.rejected, (state, action) => {
        setRowLoading(state, action.meta.arg.id, false, action.error.message);
      });
  },
});

export default organizationSlice.reducer;
